export const buildReceiptAllocationRows = (lineage = {}) => {
    const completedRefunds = (lineage.refund_sources || [])
        .filter(row => row.status === 'COMPLETED')
        .reduce((result, row) => {
            const allocationId = Number(row.source_allocation_id || 0);
            if (allocationId) {
                result[allocationId] =
                    (result[allocationId] || 0) + Number(row.amount || 0);
            }
            return result;
        }, {});

    return (lineage.allocations || [])
        .map(row => {
            const allocatedAmount = Number(row.allocated_amount || 0);
            const refundedAmount = completedRefunds[Number(row.id)] || 0;

            return {
                ...row,
                allocated_amount: allocatedAmount,
                refunded_amount: refundedAmount,
                remaining_amount: Math.max(0, allocatedAmount - refundedAmount),
            };
        })
        .filter(row => row.allocated_amount > 0);
};

export const buildReceiptContentRows = (
    items = [],
    allocationRows = [],
    serviceAllocationSummary = []
) => {
    const rows = (items || []).map(item => {
        const productRefundedAmount = item.type === 'Sản phẩm'
            ? Number(item.product_refunded_amount || 0)
            : 0;
        const productEffectiveAmount = item.type === 'Sản phẩm'
            ? Math.max(0, Number(item.product_allocated_amount || 0))
            : 0;
        const productAllocatedAmount = productEffectiveAmount + productRefundedAmount;

        return {
            ...item,
            amount: item.type === 'Sản phẩm' && item.amount_to_pay !== null
            && item.amount_to_pay !== undefined
                ? Number(item.amount_to_pay)
                : item.amount,
            receipt_paid_allocated_amount: productAllocatedAmount,
            receipt_paid_refunded_amount: productRefundedAmount,
            receipt_paid_effective_amount: productEffectiveAmount,
            allocation_ratio_percent: null,
            debt_before_amount: null,
            debt_reduced_amount: null,
            debt_after_amount: null,
        };
    });
    const receiptItemIndexes = {};
    const serviceIndexes = {};

    rows.forEach((row, index) => {
        if (row.id) receiptItemIndexes[Number(row.id)] = index;
        if (
            row.customer_service_id &&
            serviceIndexes[Number(row.customer_service_id)] === undefined
        ) {
            serviceIndexes[Number(row.customer_service_id)] = index;
        }
    });

    allocationRows.forEach(allocation => {
        let rowIndex = allocation.receipt_item_id
            ? receiptItemIndexes[Number(allocation.receipt_item_id)]
            : undefined;

        if (rowIndex === undefined && allocation.customer_service_id) {
            rowIndex = serviceIndexes[Number(allocation.customer_service_id)];
        }

        // Allocation lineage không phải nội dung chứng từ và không được dựng thành receipt item giả.
        if (rowIndex === undefined) return;

        const source = String(allocation.allocation_origin || 'OTHER');
        if (source === 'RECEIPT_AUTO') {
            rows[rowIndex].receipt_paid_allocated_amount += Number(allocation.remaining_amount || 0);
            rows[rowIndex].receipt_paid_refunded_amount += Number(allocation.refunded_amount || 0);
            rows[rowIndex].receipt_paid_effective_amount += Number(allocation.remaining_amount || 0);
        }
    });

    (serviceAllocationSummary || []).forEach(summary => {
        let rowIndex = summary.receipt_item_id
            ? receiptItemIndexes[Number(summary.receipt_item_id)]
            : undefined;
        if (rowIndex === undefined && summary.customer_service_id) {
            rowIndex = serviceIndexes[Number(summary.customer_service_id)];
        }
        if (rowIndex === undefined) return;

        rows[rowIndex].receipt_paid_allocated_amount = Number(summary.allocated_amount || 0);
        rows[rowIndex].receipt_paid_refunded_amount = Number(summary.refunded_amount || 0);
        rows[rowIndex].receipt_paid_effective_amount = Number(summary.effective_amount || 0);
        rows[rowIndex].allocation_ratio_percent = summary.allocation_ratio_percent == null
            ? null
            : Number(summary.allocation_ratio_percent);
        rows[rowIndex].debt_before_amount = summary.debt_before_amount == null
            ? null
            : Number(summary.debt_before_amount);
        rows[rowIndex].debt_reduced_amount = summary.debt_reduced_amount == null
            ? null
            : Number(summary.debt_reduced_amount);
        rows[rowIndex].debt_after_amount = summary.debt_after_amount == null
            ? null
            : Number(summary.debt_after_amount);
    });

    rows.forEach(row => {
        if (
            row.customer_service_id &&
            row.type !== 'Sản phẩm' &&
            row.debt_reduced_amount == null
        ) {
            // Dữ liệu cũ có thể thiếu snapshot nợ nhưng số tiền hiệu lực vẫn là phần giảm nợ đã biết.
            row.debt_reduced_amount = Number(row.receipt_paid_effective_amount || 0);
        }
    });

    return rows;
};

export const buildReceiptContentSummary = (rows = []) => {
    const serviceRows = rows.filter(row => row.customer_service_id && row.type !== 'Sản phẩm');
    const rowsWithDebtSnapshot = serviceRows.filter(row => row.debt_before_amount != null);
    const rowsWithRatioSnapshot = serviceRows.filter(row => row.allocation_ratio_percent != null);
    const hasCompleteDebtSnapshot = serviceRows.length > 0
        && rowsWithDebtSnapshot.length === serviceRows.length;
    const hasCompleteRatioSnapshot = serviceRows.length > 0
        && rowsWithRatioSnapshot.length === serviceRows.length;

    return {
        amount: rows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
        allocation_ratio_percent: hasCompleteRatioSnapshot
            ? rowsWithRatioSnapshot.reduce((sum, row) => sum + Number(row.allocation_ratio_percent || 0), 0)
            : null,
        allocated_amount: rows.reduce(
            (sum, row) => sum + Number(row.receipt_paid_allocated_amount || 0),
            0
        ),
        refunded_amount: rows.reduce(
            (sum, row) => sum + Number(row.receipt_paid_refunded_amount || 0),
            0
        ),
        effective_amount: rows.reduce(
            (sum, row) => sum + Number(row.receipt_paid_effective_amount || 0),
            0
        ),
        debt_before_amount: hasCompleteDebtSnapshot
            ? rowsWithDebtSnapshot.reduce((sum, row) => sum + Number(row.debt_before_amount || 0), 0)
            : null,
        debt_reduced_amount: serviceRows.reduce(
            (sum, row) => sum + Number(row.debt_reduced_amount || 0),
            0
        ),
        debt_after_amount: hasCompleteDebtSnapshot
            ? rowsWithDebtSnapshot.reduce((sum, row) => sum + Number(row.debt_after_amount || 0), 0)
            : null,
    };
};

export const formatConvertedService = service => {
    const code = String(service?.service_code || '').trim();
    const name = String(service?.service_name || '').trim();
    if (!name) return '';
    return code ? `${code}. ${name}` : name;
};
